/**
 * InitiateModificationDialog (KAN-134)
 *
 * Modal dialog for back office / admin to initiate a modification request
 * on an eligible trade. Shows trade summary, modification type radio buttons,
 * optional comment, and a warning about Cancel + New.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { modificationService } from '../../services/api/modificationService';
import { groupingsService } from '../../services/api/groupingsService';
import type { ModificationTrade, ModificationType, ModificationConfig, ConfigurableModificationType } from '../../types/modification';
import type { GroupingsConfig } from '../../types/groupings';

interface Props {
  trade: ModificationTrade;
  clientId: string;
  onSuccess: (result: any) => void;
  onCancel: () => void;
  onError: (message: string) => void;
}

const MODIFICATION_TYPES: { value: ModificationType; labelKey: string }[] = [
  { value: 'Rollover', labelKey: 'modifications.types.rollover' },
  { value: 'SettlementTypeChange', labelKey: 'modifications.types.settlementTypeChange' },
  { value: 'GroupingChange', labelKey: 'modifications.types.groupingChange' },
  { value: 'Unwind', labelKey: 'modifications.types.unwind' },
  { value: 'Cancellation', labelKey: 'modifications.types.cancellation' },
  { value: 'Other', labelKey: 'modifications.types.other' },
];

const InitiateModificationDialog: React.FC<Props> = ({
  trade,
  clientId,
  onSuccess,
  onCancel,
  onError,
}) => {
  const { t } = useTranslation();
  const [selectedType, setSelectedType] = useState<ModificationType | null>(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [modConfig, setModConfig] = useState<ModificationConfig | null>(null);
  const [groupingsConfig, setGroupingsConfig] = useState<GroupingsConfig | null>(null);
  const [priorWarning, setPriorWarning] = useState<{ priorType: string; newType: ModificationType } | null>(null);

  useEffect(() => {
    modificationService.getModificationConfig(clientId).then((res) => {
      if (res.success && res.data) setModConfig(res.data);
    }).catch(() => { /* ignore — warning simply won't show */ });
    groupingsService.getGroupings(clientId).then(setGroupingsConfig).catch(() => { /* ignore — label falls back to generic */ });
  }, [clientId]);

  const CONFIGURABLE_TYPES = new Set<ModificationType>(['Rollover', 'SettlementTypeChange', 'GroupingChange']);
  const showCancelNewWarning =
    selectedType !== null &&
    CONFIGURABLE_TYPES.has(selectedType) &&
    modConfig?.modificationTypes[selectedType as ConfigurableModificationType]?.cancelAndNew === true;

  const formatNumber = (value: any): string => {
    if (value == null || value === '') return '';
    const num = Number(value);
    if (isNaN(num)) return String(value);
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getModTypeLabel = (value: ModificationType, labelKey: string): string => {
    if (value === 'GroupingChange' && groupingsConfig) {
      const labels = [
        groupingsConfig.grouping1Label,
        groupingsConfig.grouping2Label,
        groupingsConfig.grouping3Label,
      ].filter(Boolean);
      if (labels.length > 0) {
        return `${t(labelKey).split(' ').slice(0, -1).join(' ')} ${labels.join(' / ')}`;
      }
    }
    return t(labelKey);
  };

  // Returns a human-readable label for any raw modification type string.
  const getTypeLabel = (rawType: string): string => {
    const found = MODIFICATION_TYPES.find(m => m.value === rawType);
    if (!found) return rawType;
    return getModTypeLabel(found.value, found.labelKey);
  };

  const handleSubmit = async (force = false) => {
    if (!selectedType) return;
    setSubmitting(true);
    try {
      const response = await modificationService.initiateModification(clientId, trade.id, {
        modificationType: selectedType,
        comment: comment.trim() || undefined,
        force,
      }, trade.OurPartyId);
      if (response.success) {
        // KAN-294 Fix H: backend warns when a prior modification exists
        if (response.data?.status === 'prior_modification_warning') {
          setPriorWarning({
            priorType: response.data.priorModificationType ?? '',
            newType: selectedType,
          });
          return;
        }
        onSuccess(response.data);
      } else {
        onError(response.message || t('modifications.messages.initiateError'));
      }
    } catch (err: any) {
      onError(err.message || t('modifications.messages.initiateError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return (
    <div className="modifications-modal-overlay" onClick={handleBackdropClick}>
      <div className="modifications-modal">
        {/* Header */}
        <div className="modifications-modal-header">
          <h3 className="modifications-modal-title">
            {t('modifications.initiateDialog.title')}
          </h3>
          <button className="modifications-modal-close" onClick={onCancel}>&times;</button>
        </div>

        {/* Body */}
        <div className="modifications-modal-body">
          {/* Trade summary */}
          <div className="modifications-trade-summary">
            <div className="modifications-trade-summary-field">
              <span className="modifications-trade-summary-label">{t('modifications.columns.tradeNumber')}</span>
              <span className="modifications-trade-summary-value">{trade.TradeNumber}</span>
            </div>
            <div className="modifications-trade-summary-field">
              <span className="modifications-trade-summary-label">{t('modifications.columns.state')}</span>
              <span className="modifications-trade-summary-value">{trade.workflowState}</span>
            </div>
            <div className="modifications-trade-summary-field">
              <span className="modifications-trade-summary-label">{t('modifications.columns.direction')}</span>
              <span className="modifications-trade-summary-value">{trade.Direction} {trade.ProductType}</span>
            </div>
            <div className="modifications-trade-summary-field">
              <span className="modifications-trade-summary-label">{t('modifications.columns.amount')}</span>
              <span className="modifications-trade-summary-value">
                {formatNumber(trade.QuantityCurrency1)} {trade.Currency1}
              </span>
            </div>
            <div className="modifications-trade-summary-field">
              <span className="modifications-trade-summary-label">{t('modifications.columns.counterparty')}</span>
              <span className="modifications-trade-summary-value">{trade.CounterpartyName || trade.Counterparty}</span>
            </div>
            <div className="modifications-trade-summary-field">
              <span className="modifications-trade-summary-label">{t('modifications.columns.price')}</span>
              <span className="modifications-trade-summary-value">{formatNumber(trade.Price)}</span>
            </div>
            <div className="modifications-trade-summary-field">
              <span className="modifications-trade-summary-label">{t('modifications.columns.tradeDate')}</span>
              <span className="modifications-trade-summary-value">{trade.TradeDate?.substring(0, 10)}</span>
            </div>
            <div className="modifications-trade-summary-field">
              <span className="modifications-trade-summary-label">{t('modifications.columns.maturityDate')}</span>
              <span className="modifications-trade-summary-value">{trade.MaturityDate?.substring(0, 10)}</span>
            </div>
          </div>

          {/* Modification type radio buttons */}
          <div className="modifications-radio-group">
            <span className="modifications-radio-group-label">
              {t('modifications.initiateDialog.typeLabel')}
            </span>
            {MODIFICATION_TYPES.map(({ value, labelKey }) => (
              <div key={value} className="modifications-radio-option">
                <input
                  type="radio"
                  id={`mod-type-${value}`}
                  name="modificationType"
                  value={value}
                  checked={selectedType === value}
                  onChange={() => setSelectedType(value)}
                />
                <label htmlFor={`mod-type-${value}`}>{getModTypeLabel(value, labelKey)}</label>
              </div>
            ))}
          </div>

          {/* Comment */}
          <div className="modifications-comment-section">
            <label className="modifications-comment-label">
              {t('modifications.initiateDialog.commentLabel')}
              <span style={{ color: 'var(--text-secondary)', fontWeight: 400, marginLeft: 6 }}>
                ({t('common.optional')})
              </span>
            </label>
            <textarea
              className="modifications-textarea"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('modifications.initiateDialog.commentPlaceholder')}
              rows={4}
            />
          </div>

          {/* Warning — visible only when Cancel+New is enabled for the selected type */}
          <div className="modifications-warning" style={{ visibility: showCancelNewWarning ? 'visible' : 'hidden' }}>
            <AlertTriangle size={16} className="modifications-warning-icon" />
            <span className="modifications-warning-text">
              {t('modifications.initiateDialog.warning')}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="modifications-modal-footer">
          <button className="modifications-modal-cancel-btn" onClick={onCancel}>
            {t('common.cancel')}
          </button>
          <button
            className="modifications-modal-submit-btn"
            onClick={() => handleSubmit(false)}
            disabled={!selectedType || submitting}
          >
            {submitting ? t('modifications.initiateDialog.submitting') : t('modifications.initiateDialog.submitBtn')}
          </button>
        </div>

        {/* KAN-294 Fix H: prior modification warning overlay */}
        {priorWarning && (
          <div className="modifications-modal-overlay" style={{ borderRadius: 'inherit' }} onClick={e => e.stopPropagation()}>
            <div className="modifications-modal" style={{ maxWidth: 420 }}>
              <div className="modifications-modal-header">
                <h3 className="modifications-modal-title">
                  {t('modifications.initiateDialog.priorWarning.title')}
                </h3>
              </div>
              <div className="modifications-modal-body">
                <p style={{ margin: 0 }}>
                  {t('modifications.initiateDialog.priorWarning.message', {
                    priorType: getTypeLabel(priorWarning.priorType),
                    newType: getTypeLabel(priorWarning.newType),
                  })}
                </p>
              </div>
              <div className="modifications-modal-footer">
                <button
                  className="modifications-modal-cancel-btn"
                  onClick={() => setPriorWarning(null)}
                >
                  {t('modifications.initiateDialog.priorWarning.back')}
                </button>
                <button
                  className="modifications-modal-submit-btn"
                  onClick={() => { setPriorWarning(null); handleSubmit(true); }}
                  disabled={submitting}
                >
                  {t('modifications.initiateDialog.priorWarning.confirm')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InitiateModificationDialog;
